import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message, NewMessage } from '@magic/schemas/messages';

import { request } from '../request';

export type UseChatOptions = {
  /**
   * The API endpoint that accepts a `{ messages: Message[] }` object and returns
   * a stream of tokens of the AI chat response. Defaults to `/api/chat`.
   */
  api?: string;

  /**
   * Flags the chat as a preview
   */
  preview?: boolean;

  /**
   * Initial conversation id
   */
  conversationId?: Message['conversationId'];

  /**
   * Initial messages of the chat. Useful to load an existing chat history.
   */
  initialMessages?: Message[];

  /**
   * Initial input of the chat.
   */
  initialInput?: string;

  /**
   * Callback function to be called when an error is encountered.
   */
  onError?: (error: Error) => void;
};

export type UseChatHelpers = {
  /** Current messages in the chat */
  messages: NewMessage[];

  /** The error object of the API request */
  error: undefined | Error;

  /**
   * Append a user message to the chat list. This triggers the API call to fetch
   * the assistant's response.
   * @param message The message to append
   * @param options Additional options to pass to the API call
   */
  append: (message: Message) => Promise<string | null | undefined>;

  /**
   * Abort the current request immediately, keep the generated tokens if any.
   */
  stop: () => void;

  /**
   * Update the `messages` state locally. This is useful when you want to
   * edit the messages on the client, and then trigger the `reload` method
   * manually to regenerate the AI response.
   */
  setMessages: (messages: Message[]) => void;

  /** The current value of the input */
  input: string;

  /** setState-powered method to update the input value */
  setInput: React.Dispatch<React.SetStateAction<string>>;

  /** An input/textarea-ready onChange handler to control the value of the input */
  handleInputChange: (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => void;

  /** Form submission handler to automatically reset input and append a user message */
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;

  /** Whether the API request is in progress */
  isLoading: boolean;
};

export function useChat({
  api = '/api/chat',
  initialMessages = [],
  initialInput = '',
  preview = false,
  conversationId,
  onError,
}: UseChatOptions = {}): UseChatHelpers {
  const [id, setId] = useState<Message['conversationId'] | undefined>(
    conversationId
  );
  const [messages, mutate] = useState<NewMessage[]>(initialMessages);
  const [isLoading, mutateLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Keep the latest messages in a ref.
  const messagesRef = useRef<NewMessage[]>(messages || []);
  useEffect(() => {
    messagesRef.current = messages || [];
  }, [messages]);

  // Abort controller to cancel the current API call.
  const abortControllerRef = useRef<AbortController | null>(null);

  const triggerRequest = useCallback(
    async (message: NewMessage) => {
      try {
        mutate((messages) => [message, ...messages]);
        mutateLoading(true);
        setError(undefined);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const response: Message = await request({
          api,
          method: 'POST',
          body: {
            message,
            preview,
            id,
          },
        });
        setId(response.conversationId);
        mutate((messages) => [response, ...messages]);

        abortControllerRef.current = null;
      } catch (err) {
        // Ignore abort errors as they are expected.
        if ((err as any).name === 'AbortError') {
          abortControllerRef.current = null;
          return null;
        }

        if (onError && err instanceof Error) {
          onError(err);
        }

        setError(err as Error);
      } finally {
        mutateLoading(false);
      }
    },
    [
      api,
      id,
      preview,
      mutate,
      mutateLoading,
      onError,
      setError,
      messagesRef,
      abortControllerRef,
    ]
  );

  const append = useCallback(
    async (message: Message | NewMessage) => {
      return triggerRequest(message);
    },
    [triggerRequest]
  );

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const setMessages = useCallback(
    (messages: Message[]) => {
      mutate(messages);
      messagesRef.current = messages;
    },
    [mutate]
  );

  // Input state and handlers.
  const [input, setInput] = useState(initialInput);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!input) return;

      append({
        content: input,
        role: 'user',
        createdAt: new Date(),
      });
      setInput('');
    },
    [input, append]
  );

  const handleInputChange = (e: any) => {
    setInput(e.target.value);
  };

  return {
    messages: messages || [],
    error,
    append,
    stop,
    setMessages,
    input,
    setInput,
    handleInputChange,
    handleSubmit,
    isLoading,
  };
}
